import type {
  ModalIdentifier,
  MountedModal
} from './types';

import React from 'react';
import ModalWithBackdrop from './modal_with_backdrop';
import ModalSetContext from './modal_set_context';
import getAriaProps from './get_aria_props';

import {
  setModalSetHandler,
  clearModalSetHandler
} from './modal_controller';

/**
 * Props for the ModalSetContainer component.
 */
interface ModalSetContainerProps {
  /** Unique identifier for this modal set */
  setId: ModalIdentifier;
  /** CSS class name for the container div */
  containerClassName?: string;
  /** CSS class name applied to all backdrops */
  backdropClassName?: string;
  /** CSS class name applied to backdrop when shown */
  backdropInClassName?: string;
  /** CSS class name applied to backdrop before hidden */
  backdropOutClassName?: string;
  /** CSS class name for the wrapper element */
  wrapperClassName?: string;
  /** CSS class name applied to all modals */
  modalClassName?: string;
  /** CSS class name applied to modal when shown */
  modalInClassName?: string;
  /** CSS class name applied to modal before hidden */
  modalOutClassName?: string;
  /** Delay in milliseconds before removing modal from DOM during close transition */
  outDelay?: number;
  /** Child content (not used directly, but may be passed by container) */
  children?: React.ReactNode;
}

/**
 * State for the ModalSetContainer component.
 */
interface ModalSetContainerState {
  /** Array of currently mounted modals in this set */
  modals: MountedModal[];
}

/**
 * Container for a set of modals managed together.
 *
 * This component renders multiple modals that are controlled as a group through
 * the modal controller. It handles mounting/unmounting modals and applies styling
 * to each modal instance.
 *
 * @example
 * <ModalSetContainer
 *   setId="main-modals"
 *   containerClassName="modal-container"
 *   modalClassName="modal"
 *   backdropClassName="backdrop"
 * />
 */
export default class ModalSetContainer extends React.Component<
  ModalSetContainerProps,
  ModalSetContainerState
> {
  constructor(props: ModalSetContainerProps) {
    super(props);
    this.state = {
      modals: []
    };
  }

  componentDidMount(): void {
    setModalSetHandler(this.props.setId, this.onModals);
  }

  componentWillUnmount(): void {
    clearModalSetHandler(this.props.setId);
  }

  /**
   * Callback invoked by the modal controller when modals in this set change.
   * Updates the component state to reflect the current set of mounted modals.
   *
   * @param modals The updated list of mounted modals
   */
  onModals = (modals: MountedModal[]): void => {
    this.setState({ modals });
  };

  render(): React.ReactNode {
    const {
      backdropClassName,
      backdropInClassName,
      backdropOutClassName,
      containerClassName,
      modalClassName,
      modalInClassName,
      modalOutClassName,
      wrapperClassName,
    } = this.props;

    const { modals } = this.state;

    if (modals.length === 0) {
      return null;
    }

    return (
      <div className={containerClassName}>
        {modals.map((m) => {
          // Cast m.info to any to access properties that may not be in the type definition
          // but are expected to exist at runtime
          const modalInfo = m.info as any;

          return (
            <ModalSetContext.Provider value={{ setId: m.id }} key={m.id}>
              <ModalWithBackdrop
                {...({
                  modalId: m.id,
                  backdropClassName: modalInfo.backdropClassName || backdropClassName,
                  outDelay: typeof modalInfo.outDelay === 'undefined' ? this.props.outDelay : modalInfo.outDelay,
                  backdropInClassName: modalInfo.backdropInClassName || backdropInClassName,
                  backdropOutClassName: modalInfo.backdropOutClassName || backdropOutClassName,
                  containerClassName: containerClassName,
                  modalClassName: modalInfo.className || modalClassName,
                  modalInClassName: modalInfo.inClassName || modalInClassName,
                  modalOutClassName: modalInfo.outClassName || modalOutClassName,
                  onBackdropClick: modalInfo.onBackdropClick,
                  wrapperClassName: wrapperClassName,
                  component: modalInfo.component,
                  props: modalInfo.props || {},
                  isOut: !!modalInfo.out,
                  frozenContent: modalInfo.frozenContent,
                  context: { setId: m.id },
                  ...getAriaProps(modalInfo)
                } as any)}
              >
                {modalInfo.children}
              </ModalWithBackdrop>
            </ModalSetContext.Provider>
          );
        })}
      </div>
    );
  }
}
